import React from 'react';
import PropTypes from 'prop-types';
import { useSelector } from 'react-redux';
import { FaBoxes, FaTruck, FaShareAlt, FaLeaf } from 'react-icons/fa';

// Maps legacy FontAwesome string names (from route configs) to react-icons.
const ICON_MAP = {
  boxes: FaBoxes,
  truck: FaTruck,
  'share-alt': FaShareAlt,
  leaf: FaLeaf,
};

// Soft pill colours per badge type — the clean, professional sidebar look.
const BADGE_CLS = {
  danger:  'tw-bg-rose-100 tw-text-rose-600',
  success: 'tw-bg-emerald-100 tw-text-emerald-600',
  primary: 'tw-bg-blue-100 tw-text-blue-600',
  info:    'tw-bg-sky-100 tw-text-sky-600',
  warning: 'tw-bg-amber-100 tw-text-amber-600',
};
const badgeCls = (type) => BADGE_CLS[type] || BADGE_CLS.danger;

// Reads a dynamic badge count from Redux for specific routes.
// route.badgeStore = 'my_inbox.totalRows'  → reads state.my_inbox.totalRows
const useDynamicBadge = (storePath) => {
  const value = useSelector(state => {
    if (!storePath) return null;
    const [slice, key] = storePath.split('.');
    return state[slice]?.[key] ?? null;
  });
  return value;
};

const NavbarVerticalMenuItem = ({ route }) => {
  const dynamicCount = useDynamicBadge(route.badgeStore);
  const showDynamic  = dynamicCount != null && dynamicCount > 0;

  return (
    <div className="tw-flex tw-items-center">
      {route.icon && (
        <span className="ptl-nav-link-icon">
          {/* react-icons components are functions → render directly. Legacy string
              names are mapped to react-icons via ICON_MAP.
              Sizing/colour come from `.ptl-nav-link-icon` (0.875rem, currentColor). */}
          {typeof route.icon === 'function'
            ? React.createElement(route.icon)
            : (ICON_MAP[route.icon] ? React.createElement(ICON_MAP[route.icon]) : null)}
        </span>
      )}
      <span className="ptl-nav-link-text tw-pl-1">{route.name}</span>

      {/* Static badge (e.g. "New") — soft pill */}
      {route.badge && (
        <span
          className={`tw-ml-2 tw-inline-flex tw-items-center tw-rounded-xl tw-px-2 tw-py-0.5 tw-text-[10px] tw-font-semibold tw-leading-none ${badgeCls(route.badge.type)}`}
        >
          {route.badge.text}
        </span>
      )}

      {/* Dynamic count badge — soft blue pill */}
      {showDynamic && (
        <span className="tw-ml-1.5 tw-inline-flex tw-items-center tw-justify-center tw-rounded-xl tw-min-w-[20px] tw-h-5 tw-px-1.5 tw-text-[10px] tw-font-bold tw-leading-none tw-bg-blue-100 tw-text-blue-600">
          {dynamicCount > 99 ? '99+' : dynamicCount}
        </span>
      )}
    </div>
  );
};

const routeShape = {
  active:     PropTypes.bool,
  name:       PropTypes.string.isRequired,
  to:         PropTypes.string,
  icon:       PropTypes.oneOfType([PropTypes.array, PropTypes.string, PropTypes.object]),
  badgeStore: PropTypes.string,   // e.g. "my_inbox.totalRows"
};
routeShape.children = PropTypes.arrayOf(PropTypes.shape(routeShape));
NavbarVerticalMenuItem.propTypes = {
  route: PropTypes.shape(routeShape).isRequired,
};

export default React.memo(NavbarVerticalMenuItem);
